import { ArrowRight } from "lucide-react";
import { useLang } from "@/i18n";
import { useEnquire } from "@/components/ContactModal";
import { Eyebrow, KineticLines, Reveal } from "@/components/motion";

const BiVenti = () => {
  const { t } = useLang();
  const { setOpen } = useEnquire();
  const v = t.biventi;

  return (
    <main data-testid="biventi-page">
      {/* HERO */}
      <section data-testid="biventi-hero" className="relative flex items-end pt-36">
        <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 pb-16 lg:px-12">
          <Eyebrow>{v.eyebrow}</Eyebrow>
          <h1 className="mt-4">
            <KineticLines
              lines={v.titleLines}
              delay={0.2}
              className="font-tech text-[13vw] font-bold uppercase leading-[0.95] tracking-tight sm:text-[10vw] lg:text-[8rem]"
            />
          </h1>
        </div>
      </section>

      {/* CONCEPT CHAPTERS */}
      <section data-testid="biventi-chapters" className="mx-auto max-w-[1440px] px-6 py-16 lg:px-12">
        <div className="grid gap-16 md:grid-cols-3">
          {v.chapters.map((c) => (
            <Reveal key={c.n} testId={`biventi-chapter-${c.n}`}>
              <span className="font-tech text-5xl font-bold text-outline">{c.n}</span>
              <h2 className="mt-6 font-heritage text-3xl italic tracking-tight sm:text-4xl">{c.title}</h2>
              <p className="mt-6 text-base leading-relaxed text-neutral-400">{c.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SPECS BENTO */}
      <section data-testid="biventi-specs" className="border-y border-white/10 py-20">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
          <Reveal>
            <Eyebrow>{v.specsLabel}</Eyebrow>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 gap-px bg-white/10 md:grid-cols-3" data-testid="biventi-specs-grid">
            {v.specs.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.05} className="bg-[#10161E] p-8 lg:p-12">
                <p className="font-tech text-[10px] uppercase tracking-[0.35em] text-white/40">{s.label}</p>
                <p className="mt-4 font-tech text-xl uppercase leading-snug tracking-wide text-white lg:text-2xl">
                  {s.value}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section data-testid="biventi-gallery" className="mx-auto max-w-[1440px] space-y-14 px-6 pb-20 lg:px-12">
        {v.gallery.map((g, i) => (
          <Reveal key={g.caption} testId={`biventi-gallery-${i}`}>
            <img src={g.src} alt={g.caption} className="w-full border border-white/10" />
            <p className="mt-6 font-tech text-xs uppercase tracking-[0.35em] text-white/50">{g.caption}</p>
          </Reveal>
        ))}
      </section>

      {/* ALLOCATION CTA */}
      <section data-testid="biventi-cta" className="border-t border-white/10 py-20">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
          <Reveal>
            <h2 className="max-w-3xl font-heritage text-4xl italic leading-tight text-white/90 sm:text-6xl">
              {v.cta.title}
            </h2>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-neutral-400">{v.cta.text}</p>
            <button
              data-testid="biventi-allocation-button"
              onClick={() => setOpen(true)}
              className="group mt-12 inline-flex items-center gap-3 rounded-full bg-[#4A7BA6] px-10 py-4 font-tech text-sm uppercase tracking-[0.3em] text-white transition-colors duration-300 hover:bg-white hover:text-black"
            >
              {v.cta.button}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default BiVenti;
